import React, { useCallback, useMemo, useRef, useState } from "react";
import _, { find, flatMap, isUndefined, uniq } from "lodash";
import { useFindingDetails } from "./FindingDetailsProvider";
import FindingAPI from "restAPI/FindingAPI";
import message from "components/CustomMessage";
import { Collapse, Spin } from "antd";
import { ARMORCODE_PRIMARY_CORRELATED_FINDING, RESERVED_TAG_KEYS } from "../Constants";
import ACTag from "ac-components/ac-tag/ACTag";
import { getVocabulary } from "components/Vocabulary";
import ACSettingSectionTitle from "ac-components/ac-setting-section-title/ACSettingSectionTitle";
import ACTagTableOld from "ac-components/ac-tag-table/ACTagTableOld";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { getFindingDetailsHideArmorCodeTags, setFindingDetailsHideArmorCodeTags } from "utils/localStorageUtils";
import { Checkbox, Input } from "antd-v5";
import useDebounceCallback from "hooks/useDebounceCallback";
import SearchIcon from "ac-components/ac-icons/SearchIcon";

export function FindingDetailsTag({ designV3 = true }) {
  const featureFlags = useFeatureFlag();
  const findingsTagSuccessMsgRef = useRef();

  const { loading, finding, findingTagsRef, fetchFinding } = useFindingDetails();
  const [checkedVal, setCheckedVal] = useState(() => getFindingDetailsHideArmorCodeTags() ?? true);
  const [searchTerm, setSearchTerm] = useState("");
  const onSearch = useDebounceCallback((e) => setSearchTerm(e.target.value), []);

  const handleHideArmorCodeTagsChange = (e) => {
    const checked = e.target.checked;
    setCheckedVal(checked);
    setFindingDetailsHideArmorCodeTags(checked);
  };

  const header = "TAGS";

  const staticTags = useMemo(() => {
    const tagsMetaMap = finding?.tagsMetaMap;
    let tags = [];
    if (tagsMetaMap) {
      const requiredTagsMeta = _.omit(tagsMetaMap, ["FINDING"]);
      // The ASSETS value is array of object while rest are array of string
      if (requiredTagsMeta.ASSETS) {
        requiredTagsMeta.ASSETS = _.flatten(
          _.map(requiredTagsMeta.ASSETS, (i) => i.assetTags)
        );
      }
      tags = _.flatten(_.values(requiredTagsMeta));

      // ENG-59611 Do not allow armorcode.primary_correlated_finding to be edited
      const findingTags = _.get(tagsMetaMap, ["FINDING"]);
      const armorcodeCorrelatedTags = _.filter(findingTags, tag => tag.indexOf(ARMORCODE_PRIMARY_CORRELATED_FINDING) !== -1);
      if (!_.isEmpty(armorcodeCorrelatedTags)) {
        tags.push(...armorcodeCorrelatedTags);
      }
    }
    return tags;
  }, [finding]);

  const onTagsChange = useCallback(
    async (tags) => {
      const findingIds = [finding.id];
      const params = {
        findingTags: _.map(
          tags,
          (tag) => `${tag.key}${tag.value ? `:${tag.value}` : ""}`
        ),
        findingIds,
      };
      if (findingsTagSuccessMsgRef.current) {
        findingsTagSuccessMsgRef.current();
      }
      await FindingAPI.bulk.tagUpdate(params).promise;
      findingsTagSuccessMsgRef.current = message.success(
        "Finding tags updated successfully."
      );
      fetchFinding();
    },
    [finding, fetchFinding]
  );

  const tagsMetaMap = useMemo(() => {
    const base = {
      ...finding?.tagsMetaMap,
      ASSETS: uniq(
        flatMap(finding?.tagsMetaMap?.ASSETS, (asset) => asset?.assetTags)
      ),
    };
    if (!checkedVal) return base;
    return _.mapValues(base, (tags) =>
      _.isArray(tags)
        ? tags.filter((tag) => typeof tag === "string" ? !tag.startsWith("armorcode.") : true)
        : tags
    );
  }, [finding?.tagsMetaMap, checkedVal]);

  const tabsList = [
    {
      label: "All",
      value: "ALL",
    },
    {
      label: "Finding",
      value: "FINDING",
    },
    {
      label: "Source Tool",
      value: "TOOL",
    },
    {
      label: getVocabulary("product"),
      value: "PRODUCT",
    },
    {
      label: getVocabulary("subProduct"),
      value: "SUBPRODUCT",
    },
    {
      label: "Assets",
      value: "ASSETS",
    },
    {
      label: getVocabulary("assessment"),
      value: "ASSESSMENT",
      optional: true,
    },
    ...(featureFlags.AATI_SCORE_COLUMN
      ? [
          {
            label: "Feed",
            value: "FEED",
          },
        ]
      : []),
  ];

  const extraColumn = (tab) =>
    tab === "ALL"
      ? [
          {
            title: "Origin",
            dataIndex: "origin",
            key: "origin",
            width: 150,
            sorter: true,
            render: (origin, currentrecord) => {
              const value = !isUndefined(origin) ? origin : getOrigin(currentrecord, tagsMetaMap);
              return (
                <ACTag
                  label={find(tabsList, { value: value })?.label ?? value}
                />
              );
            },
          },
        ]
      : [];

  const filteredTags = useMemo(() => {
    if (checkedVal && finding?.tags) {
      return finding.tags.filter(tag => !tag.startsWith("armorcode."));
    }
    return finding?.tags;
  }, [checkedVal, finding?.tags]);
    
  const tagSection = (
    <Spin spinning={loading}>
      <div>
        <ACSettingSectionTitle
          description={`This finding inherits tags from it's ${getVocabulary(
            "product"
          )}, ${getVocabulary(
            "subProduct"
          )} and Source as well as having it's own tags.`}
          title={
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-8">
                <span>Manage Tags</span>
                <div style={{userSelect: "none"}}>
                  <Checkbox
                    checked={checkedVal}
                    onChange={handleHideArmorCodeTagsChange}
                    className="text-default"
                  >
                    Hide ArmorCode Tags
                  </Checkbox>
                </div>
              </div>
              <Input
                prefix={<SearchIcon />}
                placeholder="Search tags"
                onChange={onSearch}
                allowClear
                style={{ width: 162 }}
              />
            </div>
          }
        />
        <ACTagTableOld
          className="p-sm"
          tabsList={tabsList}
          extraColumn={(tab) => extraColumn(tab)}
          source={filteredTags}
          staticTags={staticTags}
          tagsMetaMap={tagsMetaMap}
          editable
          onChange={onTagsChange}
          tagsRef={findingTagsRef}
          preventKeys={RESERVED_TAG_KEYS}
          finding={finding}
          searchTerm={searchTerm}
        />
      </div>
    </Spin>
  );

  return (
    <>
      {designV3 ? (
        tagSection
      ) : (
        <Collapse className="collapse-v2">
          <Collapse.Panel header={header}>{tagSection}</Collapse.Panel>
        </Collapse>
      )}
    </>
  );
}

export const getOrigin = (item, tagsMetaMap) => {
  const tagsMetaMapKeys = [
    "FINDING",
    "ASSESSMENT",
    "ASSETS",
    "SUBPRODUCT",
    "PRODUCT",    
    "TOOL",
    "FEED",
  ];
  const exactMatch = tagsMetaMapKeys.find((key) =>
    tagsMetaMap[key]?.includes(`${item.key}:${item.value}`)
  );
  const partialKeyMatch = tagsMetaMapKeys.find((key) =>
    tagsMetaMap[key]?.includes(item.key)
  );
  const value = exactMatch || partialKeyMatch || "FINDING";
  return value;
};
